import { heIL } from "@material-ui/core/locale"
import { createTheme, ThemeOptions } from "@material-ui/core/styles"
import createBreakpoints from "@material-ui/core/styles/createBreakpoints"
import { Shadows } from "@material-ui/core/styles/shadows"
import { Font } from "components/shopFeatures/wizard/constants"

const colors = {
  //
  black: "#000000",
  white: "#ffffff",
  chineseBlack: "#161616",
  charlestonGrey: "#2b2b2b",
  borderGrey: "#d5d5d5",
  lightGrey: "#f5f5f5",
  mazdaRed: "#920a2d",
  arsenic: "#414141",
  philippineGray: "#808080",
  cssYellow: "yellow",
}

const breakpoints = createBreakpoints({})

const muiThemeOptions: ThemeOptions = {
  direction: "rtl",

  palette: {
    primary: {
      main: colors.charlestonGrey,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.white,
      contrastText: colors.chineseBlack,
    },
    text: {
      primary: colors.chineseBlack,
      secondary: colors.chineseBlack,
    },
    error: {
      main: "#b50303",
    },
  },
  shadows: new Array(25).fill("none") as Shadows, // disable all shadows
  shape: { borderRadius: 2 },
  typography: {
    fontFamily: "MazdaType, OENaPro, sans-serif;",
    h2: {
      fontSize: "1.125rem",
      fontWeight: 300,
      letterSpacing: "0.72px",
      margin: "16px 0",

      [breakpoints.up("md")]: {
        fontSize: "1.25rem",
      },
    },
    h3: {
      fontSize: "0.875rem",
      fontWeight: 600,
      letterSpacing: "0.56px",
      marginTop: "24px",
      marginBottom: "20px",
      borderBottom: `1px solid ${colors.chineseBlack}`,
      paddingBottom: "8px",

      [breakpoints.up("md")]: {
        fontSize: "1rem",
      },
    },
    h4: {
      fontSize: "0.875rem",
      fontWeight: 600,
      letterSpacing: "0.84px",
      marginTop: "20px",
      marginBottom: "8px",
      color: colors.chineseBlack,
      [breakpoints.up("md")]: {
        fontSize: "1rem",
      },
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.43",
      letterSpacing: "0.1em",
      color: colors.black,
    },
  },

  props: {
    MuiFormControl: {
      fullWidth: true,
    },
    MuiTextField: {
      fullWidth: true,
    },
    // Removed to make forms shrink labels
    // MuiInputLabel: {
    //   shrink: true,
    // },
    MuiInput: {
      disableUnderline: true,
    },
    MuiLink: {
      underline: "always",
    },
  },
}

const muiTheme = createTheme(muiThemeOptions, heIL)

muiThemeOptions.overrides = {
  MuiButton: {
    containedPrimary: {
      backgroundColor: colors.mazdaRed,
      color: colors.white,
      fontSize: "1rem",
      fontWeight: 600,
      letterSpacing: "0.64px",
      "&:hover": {
        backgroundColor: colors.mazdaRed,
      },
      "@media (hover: none)": {
        "&&:hover": {
          backgroundColor: colors.mazdaRed,
        },
      },
      "&:focus": {
        backgroundColor: colors.white,
        color: colors.mazdaRed,
        outline: `2px solid ${colors.mazdaRed}`,
      },
    },
    root: {
      "&:focus": {
        outline: "1px solid currentColor",
      },
    },
  },
  MuiIconButton: {
    colorSecondary: {
      backgroundColor: colors.philippineGray,
      "&:hover": colors.philippineGray,
    },
    root: {
      "&:hover": colors.philippineGray,
    },
  },
  MuiTouchRipple: {
    rippleVisible: {
      opacity: 0.15,
      animation: `$enter 550ms ${muiTheme.transitions.easing.easeInOut}`,
    },
  },
  MuiOutlinedInput: {
    root: {
      borderRadius: "3px",
    },
  },
  MuiInputLabel: {
    outlined: {
      transform: "translate(12px, 34px) scale(1)",
    },
    formControl: {
      position: "static",
    },
    shrink: {
      transform: "none",
    },
  },
  MuiFormLabel: {
    root: {
      fontFamily: `${Font.OENaPro}`,
      fontSize: "16px",
      fontWeight: 300,
      lineHeight: "22px",
      letterSpacing: "0em",

      color: colors.chineseBlack,

      "&$error": {
        color: colors.chineseBlack,
      },
      "& + .MuiInput-formControl": {
        marginTop: "6px",
      },
    },
    asterisk: {
      color: muiTheme.palette.error.main,
    },
  },
  MuiInput: {
    input: {
      border: `1px solid ${colors.chineseBlack}`,
      borderRadius: muiTheme.shape.borderRadius,
      height: "40px",
      boxSizing: "border-box",
      padding: muiTheme.spacing(1),
    },
    root: {
      "&$disabled": {
        color: colors.chineseBlack,
      },
    },
  },

  MuiFormControl: {
    root: {
      marginBottom: muiTheme.spacing(0.5),
    },
  },
  MuiFormHelperText: {
    root: {
      marginTop: 0,
    },
  },
  MuiDialog: {
    paper: {
      margin: "12px",

      "&.authDialog": {
        width: "328px !important",
      },

      "&.leadForm": {
        width: "670px !important",
        maxWidth: "670px",
      },
      "&.summaryTestDrive": {
        width: "670px !important",
        maxWidth: "670px",
        padding: "0 30px",
      },
    },
    paperWidthMd: {
      width: "560px",
    },
  },
  MuiCheckbox: {
    colorPrimary: {
      "&$checked": {
        color: colors.black,
      },
    },
    root: {
      "&.orderCheckbox + span": {
        fontFamily: `${Font.OENaPro}`,
        fontSize: "12px",
        fontWeight: "300",
        lineHeight: "16px",
        letterSpacing: "0em",
      },
    },
  },
  MuiSelect: {
    select: {
      borderRadius: "3px",
      height: "45px !important",
      border: "1px solid #999",
      display: "flex",
      alignItems: "center",

      "&:focus": {
        borderRadius: "3px",
        height: "45px !important",
        border: "1px solid #999",
      },
    },
  },
  MuiInputBase: {
    root: {
      marginTop: "0 !important",
    },
    input: {
      height: "45px !important",
      padding: "0 10px !important",
    },
  },
  MuiPaper: {
    root: {
      width: "670px !important",
    },
  },
  MuiTextField: {
    root: {
      "&.orderField input": {
        direction: "ltr !important",
      },
    },
  },
}

const shopTheme = {
  ...createTheme(muiThemeOptions, heIL),
  colors,
}

export default shopTheme

export type ShopTheme = typeof shopTheme
